import { useMutation } from '@apollo/client';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import { FileCopyOutlined } from '@material-ui/icons';
import React, { useCallback } from 'react';

import { AIInterviewRequest, CREATE_AI_INTERVIEW_REQUEST } from '../graphql/queries/ai-interview-requests';
import { useSnackbar } from '../hooks/use-snackbar';

interface PersonAIInterviewProps {
    personId: string;
    jobId: string;
    request?: AIInterviewRequest;
    onCreated: () => Promise<unknown>;
}

export const PersonAIInterview: React.FC<PersonAIInterviewProps> = ({ personId, jobId, request, onCreated }) => {
    const [createInterview, { loading: creating }] = useMutation<
        { createAIInterviewRequest: string },
        { personId: string; jobId: string }
    >(CREATE_AI_INTERVIEW_REQUEST);
    const { setSnackbar } = useSnackbar();
    const interviewUrl = request?.interviewUrl;

    const handleCreateInterview = async () => {
        try {
            setSnackbar('Requesting AI Interview...');
            await createInterview({ variables: { personId, jobId } });
            await onCreated();
            setSnackbar('AI Interview Requested');
        } catch {
            setSnackbar('Failed to request AI Interview');
        }
    };

    const handleCopyInterviewUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(interviewUrl);
            setSnackbar('Copied AI Interview link to clipboard');
        } catch {
            setSnackbar('Failed to copy AI Interview link');
        }
    }, [interviewUrl, setSnackbar]);

    const createButton = !request ? (
        <Button onClick={handleCreateInterview} disabled={creating}>
            Request AI Interview
        </Button>
    ) : null;

    const content = request ? (
        <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Button href={interviewUrl} target="_blank" rel="noreferrer">
                AI Interview Link
            </Button>
            <Tooltip title="Copy to clipboard">
                <IconButton
                    size="small"
                    onClick={handleCopyInterviewUrl}
                    aria-label="Copy AI Interview link"
                    style={{ marginLeft: 8 }}
                >
                    <FileCopyOutlined fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    ) : (
        createButton
    );

    return <div>{content}</div>;
};
